import React, { act, useEffect, useRef, useState } from "react";
import { connect, useSelector } from "react-redux";
import { PageHeader } from "@ant-design/pro-components";
import {
    Layout,
    Breadcrumb,
    Button,
    ConfigProvider,
    Avatar,
    Card,
    Tooltip,
    notification,
    Dropdown,
    Tag,
    Space,
    Typography,
} from "antd";
import { ProTable } from "@ant-design/pro-components";
import { BellOutlined, DashboardOutlined, FileSyncOutlined, PlusOutlined } from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";
import ptBRIntl from "antd/lib/locale/pt_BR";
import moment from "moment";
import "moment/locale/pt-br";
import "reactjs-popup/dist/index.css";

/**
 * Actions
 */
import { allActivitiesForms } from "auth/actions/activitieActions";

/**
 * Components
 */
import TopBar from "components/NavBar/TopBar";
import DrawerSider from "components/NavBar/DrawerSider";
import { LayoutTopSide } from "components/NavBar/styles";

/**
 * Utils
 */
import { getAutomations } from "../../../auth/actions/automationsActions";
import { triggers_type } from "../Edit/Flow/constants/triggers";
import PermissionWrapperComponent from "../../../components/PermissionWrapperComponent";
import { usePersistentFilters } from "utils/use-persistents-filters";
import { defaultTablePagination } from "utils/table-pagination";


const { Text } = Typography;

// Nome amigável do formulário: o prefixo técnico "obj_" não interessa a quem lê
const formatFormName = (name) => (name || "").replace("obj_", "");

// Gatilho da automação conforme cadastrado; a listagem só recebe type/config
const getTriggerInfo = (trigger) => {
    const type = trigger && trigger.type ? trigger.type : null;
    if (!type) {
        return null;
    }
    const definition = triggers_type.find((item) => item.type === type);
    return {
        type,
        name: definition ? definition.name : type,
        icon: definition ? definition.icon : null,
    };
};

// Formulários monitorados pelo gatilho. Hoje só os gatilhos de registro
// apontam formulário (config.form); webhook e cron não possuem.
const getTriggerForms = (trigger) => {
    const config = trigger && trigger.config ? trigger.config : null;
    if (!config) {
        return [];
    }
    const forms = Array.isArray(config.forms) ? config.forms : [config.form];
    return forms
        .filter((form) => form && (form.name || form.id))
        .map((form) => ({
            id: form.id,
            name: formatFormName(form.name) || String(form.id),
        }));
};

// Array responsavel para incluir filtros persistentes
const filterConfig = [
    {
        field: 'name',
        queryParam: 'nameAutomation',
        localKey: 'nameAutomation',
        type: 'text'
    },
    {
        field: 'active',
        queryParam: 'actionAutomation',
        localKey: 'actionAutomation',
        type: 'text'
    },
]

function AllAutomations({ user }) {
    const navigate = useNavigate();
    const location = useLocation();

    const actionRef = useRef();
    const formRef = useRef();

    const routes = [
        {
            breadcrumbName: "Automações",
            href: "/automacoes",
        },
        {
            breadcrumbName: "todas",
        },
    ];

    const company = useSelector((state) => state.user.company);

    const { onSubmitFilters, onReset } = usePersistentFilters(filterConfig, actionRef, formRef, location, navigate);

    const columns = [
        {
            sorter: true,
            title: "Nome",
            dataIndex: "name",
            key: "name",
            render: (dom, record, index, action, schema) => {
                const text = record[schema.dataIndex];
                if (text) {
                    return (
                        <Button
                            type="link"
                            style={{
                                color: "#0e9c84",
                            }}
                            onClick={() => navigate(`/automacoes/editar/${record.id}`)}
                        >
                            {text.replace("obj_", "")}
                        </Button>
                    );
                }

            },
            width: "auto",
            ellipsis: true,
            valueType: "text",
            fieldProps: {
                placeholder: "",
            },
        },
        {
            sorter: true,
            title: "Descrição",
            dataIndex: "description",
            key: "description",
            valueType: "text",
            responsive: ["md"],
            fieldProps: {
                placeholder: "",
            },
            render: (dom, record, index, action, schema) => {
                const text = record[schema.dataIndex];
                if (text) {
                    return (<Tooltip placement="bottom" title={text}>
                        <Button>{text.substring(0, 35) + "..."}</Button>
                    </Tooltip>)
                }

            },
            width: "auto",
            ellipsis: true,
            search: false
        },
        {
            title: "Gatilho",
            dataIndex: "trigger",
            key: "trigger",
            search: false,
            sorter: false,
            width: "auto",
            ellipsis: true,
            render: (dom, record) => {
                const trigger = getTriggerInfo(record.trigger);
                if (!trigger) {
                    return <Text type="secondary">Não configurado</Text>;
                }
                return (
                    <Tag style={{ marginInlineEnd: 0 }}>
                        <Space size={4}>
                            {trigger.icon}
                            {trigger.name}
                        </Space>
                    </Tag>
                );
            },
        },
        {
            title: "Formulários",
            dataIndex: "triggerForms",
            key: "triggerForms",
            search: false,
            sorter: false,
            responsive: ["md"],
            width: "auto",
            ellipsis: true,
            render: (dom, record) => {
                const forms = getTriggerForms(record.trigger);
                if (forms.length === 0) {
                    return <Text type="secondary">—</Text>;
                }
                return (
                    <Space size={4}>
                        {forms.map((form) => (
                            <Tooltip key={form.id || form.name} placement="bottom" title={form.name}>
                                <Tag color="blue" style={{ marginInlineEnd: 0 }}>
                                    {form.name}
                                </Tag>
                            </Tooltip>
                        ))}
                    </Space>
                );
            },
        },
        {
            sorter: true,
            title: "Status",
            dataIndex: "active",
            key: "active",
            valueEnum: {
                null: "Todos",
                true: "Ativo",
                false: "Inativo"
            },
            valueType: "select",
            fieldProps: {
                placeholder: "",
            },
            width: "auto",
            ellipsis: true,
        },
        {
            sorter: true,
            title: "Criado em",
            dataIndex: "createdAt",
            key: "createdAt",
            responsive: ["lg"],
            render: (dom, entity, index, action, schema) => {
                if (action) {
                    return moment(entity[schema.dataIndex], "YYYY-MM-DDTHH:mm:ss[Z]").format("DD/MM/YYYY HH:mm");
                }
            },
            width: "auto",
            ellipsis: true,
            search: false
        },
        {
            sorter: true,
            title: "Atualizado em",
            dataIndex: "updatedAt",
            key: "updatedAt",
            responsive: ["lg"],
            render: (dom, entity, index, action, schema) => {
                if (action) {
                    return moment(entity[schema.dataIndex], "YYYY-MM-DDTHH:mm:ss[Z]").format("DD/MM/YYYY HH:mm");
                }
            },
            width: "auto",
            ellipsis: true,
            search: false
        }

    ];


    return (
        <LayoutTopSide>
            <TopBar />
            <Layout style={{ overflow: "hidden" }}>
                <DrawerSider />
                <Layout
                    style={{
                        padding: "0 24px 24px",
                        height: "100%",
                        overflowY: "auto",
                    }}
                >
                    <PageHeader
                        style={{ margin: "16px 0 5px 0" }}
                        title="Lista de automações"
                        subTitle="Crie e gerencie automações para monitorar seus formulários e disparar alertas."
                        breadcrumb={
                            <Breadcrumb>
                                {routes.map((a) =>
                                    a.hasOwnProperty("href") ? (
                                        <Breadcrumb.Item key={Math.random()}>
                                            <a href onClick={() => navigate(a.href)}>
                                                {a.breadcrumbName}
                                            </a>
                                        </Breadcrumb.Item>
                                    ) : (
                                        <Breadcrumb.Item>{a.breadcrumbName}</Breadcrumb.Item>
                                    )
                                )}
                            </Breadcrumb>
                        }
                    />
                    <Card>
                        <ConfigProvider locale={ptBRIntl}>
                            <ProTable
                                rowKey="_id"
                                type="table"
                                pagination={defaultTablePagination}
                                onSubmit={onSubmitFilters}
                                actionRef={actionRef}
                                onReset={onReset}
                                formRef={formRef}
                                request={async (params = {}, sorter, filter) => {
                                    let { active = null, name } = params;
                                    const data = {
                                        unitId: company.unitId
                                    };
                                    active = JSON.parse(active);
                                    if (active !== null) {
                                        data['active'] = Number(active);
                                    }

                                    if (name) {
                                        data['name'] = name;
                                    }

                                    const sortEntries = Object.entries(sorter || {});
                                    if (sortEntries.length > 0) {
                                        const [field, value] = sortEntries[0];
                                        if (field && value) {
                                            data["orderByField"] = field;
                                            data["orderBySort"] = value.replace("end", "");
                                        }
                                    }

                                    const response = await getAutomations({
                                        ...data,
                                        page: params.current,
                                        perPage: params.pageSize
                                    });

                                    return {
                                        data: response.data,
                                        success: true,
                                        total: response.total,
                                    };
                                }}
                                columns={columns}
                                dateFormatter={"number"}
                                scroll={{ x: true }}
                                style={{ padding: 0 }}
                                toolbar={{
                                    actions: [
                                        // Primário (cheio/azul) + ícone + canto arredondado
                                        <PermissionWrapperComponent permissions={['CREATE_AUTOMATION']}>
                                            <Button
                                                type="primary"
                                                icon={<PlusOutlined />}
                                                shape="round"
                                                onClick={() => navigate(`/automacoes/nova`)}
                                            >
                                                Adicionar
                                            </Button>
                                        </PermissionWrapperComponent>,

                                        // Borda tracejada (dashed) + ícone
                                        <PermissionWrapperComponent permissions={['VIEW_LOG_AUTOMATION']}>
                                            <Button
                                                type="dashed"
                                                icon={<DashboardOutlined />}
                                                shape="round"
                                                onClick={() => navigate('/automacoes/dashboard')}
                                            >
                                                Dashboard de Execução
                                            </Button>
                                        </PermissionWrapperComponent>,

                                        <PermissionWrapperComponent permissions={[
                                            'EDIT_AUTOMATION',
                                            'DELETE_AUTOMATION',
                                            'VIEW_LOG_AUTOMATION'
                                        ]}>
                                            <Button
                                                type="primary"
                                                ghost
                                                icon={<BellOutlined />}
                                                shape="round"
                                                onClick={() => navigate('/automacoes/alertas')}
                                            >
                                                Configuração de Alertas
                                            </Button>
                                        </PermissionWrapperComponent>,
                                    ],
                                }}
                            />
                        </ConfigProvider>
                    </Card>
                </Layout>
            </Layout>
        </LayoutTopSide>
    );
}

const mapStateToProps = ({ session }) => ({
    user: session.user,
});

export default connect(mapStateToProps)(AllAutomations);
